import React, { useState, useEffect } from 'react';
import { tutorApi } from '../services/api';
import '../assets/LessonOverview.css';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const FULL_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_CONFIG = {
  endorsed: { label: 'Endorsed', className: 'status-endorsed' },
  disputed: { label: 'Disputed', className: 'status-disputed' },
  pending: { label: 'Pending', className: 'status-pending' },
};

const PAYMENT_CONFIG = {
  paid: { label: 'Paid', className: 'pay-paid' },
  unpaid: { label: 'Unpaid', className: 'pay-unpaid' },
};

const LessonOverview = ({ onSelectMonth }) => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterYear, setFilterYear] = useState('all');
  const [filterEndorsement, setFilterEndorsement] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');

  // Bar chart tooltip
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await tutorApi.getMonthlySummaries();
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      // Filter: only show months up to and including the current month,
      // and only months that actually have lessons
      const filtered = (response.data || []).filter(s => {
        if (s.year > currentYear) return false;
        if (s.year === currentYear && s.month > currentMonth) return false;
        if (!s.totalLessons || s.totalLessons === 0) return false;
        return true;
      });

      setSummaries(filtered);
    } catch (err) {
      setError('Failed to load lesson history.');
      console.error('Error fetching summaries:', err);
    } finally {
      setLoading(false);
    }
  };

  // --- Derived data ---

  // All available years from summaries
  const availableYears = [...new Set(summaries.map(s => s.year))].sort((a, b) => b - a);

  // Chart: last 6 months (most recent first → reverse for chart left-to-right)
  const chartData = [...summaries]
    .sort((a, b) => (b.year !== a.year ? b.year - a.year : b.month - a.month))
    .slice(0, 6)
    .reverse();

  const maxEarnings = Math.max(...chartData.map(s => s.totalEarnings || 0), 1);

  // Filtered table data
  const filteredSummaries = summaries.filter(s => {
    if (filterYear !== 'all' && s.year !== parseInt(filterYear)) return false;
    if (filterEndorsement !== 'all' && s.endorsementStatus !== filterEndorsement) return false;
    if (filterPayment !== 'all' && s.paymentStatus !== filterPayment) return false;
    return true;
  });

  const handleBarClick = (s) => {
    onSelectMonth(s.month, s.year);
  };

  const handleRowClick = (s) => {
    onSelectMonth(s.month, s.year);
  };

  if (loading) {
    return (
      <div className="lo-loading">
        <div className="lo-spinner" />
        <p>Loading lesson history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lo-error">
        <p>{error}</p>
        <button onClick={fetchSummaries} className="lo-btn-retry">Retry</button>
      </div>
    );
  }

  return (
    <div className="lo-container">
      <div className="lo-page-header">
        <h2>Lesson Summary</h2>
        <p className="lo-subtitle">Overview of your teaching history and earnings</p>
      </div>

      {/* ── Bar Chart ── */}
      {chartData.length > 0 ? (
        <div className="lo-chart-card">
          <div className="lo-chart-header">
            <h3>Earnings — Last {chartData.length} Month{chartData.length !== 1 ? 's' : ''}</h3>
          </div>
          <div className="lo-chart-body">
            <div className="lo-chart-bars">
              {chartData.map((s) => {
                const pct = maxEarnings > 0 ? ((s.totalEarnings || 0) / maxEarnings) * 100 : 0;
                const label = `${MONTH_NAMES[s.month - 1]} ${s.year}`;
                const fillClass = s.endorsementStatus === 'endorsed' ? 'endorsed' : s.endorsementStatus === 'disputed' ? 'disputed' : 'pending';
                return (
                  <div
                    key={`${s.year}-${s.month}`}
                    className="lo-bar-col"
                    onClick={() => handleBarClick(s)}
                    title={`${label}: $${(s.totalEarnings || 0).toFixed(2)}`}
                  >
                    {/* Amount above bar — desktop */}
                    <div className="lo-bar-amount lo-bar-amount-top">${(s.totalEarnings || 0).toFixed(0)}</div>
                    <div className="lo-bar-track">
                      <div
                        className={`lo-bar-fill ${fillClass}`}
                        style={{ height: `${Math.max(pct, 4)}%` }}
                      >
                        {/* Amount inside bar — mobile overlay */}
                        <span className="lo-bar-amount-inside">${(s.totalEarnings || 0).toFixed(0)}</span>
                      </div>
                    </div>
                    <div className="lo-bar-label">{label}</div>
                  </div>
                );
              })}
            </div>
            <div className="lo-chart-legend">
              <span className="lo-legend-item"><span className="lo-legend-dot endorsed" />Endorsed</span>
              <span className="lo-legend-item"><span className="lo-legend-dot pending" />Pending</span>
              <span className="lo-legend-item"><span className="lo-legend-dot disputed" />Disputed</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="lo-chart-card lo-chart-empty">
          <p>No lesson data yet. Start submitting lessons to see your earnings chart.</p>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="lo-filters-card">
        <div className="lo-filters-row">
          <div className="lo-filter-group">
            <label>Year</label>
            <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
              <option value="all">All Years</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="lo-filter-group">
            <label>Endorsement</label>
            <select value={filterEndorsement} onChange={e => setFilterEndorsement(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="endorsed">Endorsed</option>
              <option value="disputed">Disputed</option>
            </select>
          </div>
          <div className="lo-filter-group">
            <label>Payment</label>
            <select value={filterPayment} onChange={e => setFilterPayment(e.target.value)}>
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          {(filterYear !== 'all' || filterEndorsement !== 'all' || filterPayment !== 'all') && (
            <button
              className="lo-btn-clear"
              onClick={() => { setFilterYear('all'); setFilterEndorsement('all'); setFilterPayment('all'); }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="lo-table-card">
        {filteredSummaries.length === 0 ? (
          <div className="lo-table-empty">
            <p>No records match the selected filters.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="lo-table-hint">Click a row to view the full monthly summary</div>
            <div className="lo-table-wrapper lo-desktop-only">
              <table className="lo-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Lessons</th>
                    <th>Earnings</th>
                    <th>Endorsement</th>
                    <th>Payment</th>
                    <th className="lo-col-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSummaries.map((s) => {
                    const endCfg = STATUS_CONFIG[s.endorsementStatus] || STATUS_CONFIG.pending;
                    const payCfg = PAYMENT_CONFIG[s.paymentStatus] || PAYMENT_CONFIG.unpaid;
                    return (
                      <tr
                        key={`${s.year}-${s.month}`}
                        className="lo-table-row"
                        onClick={() => handleRowClick(s)}
                      >
                        <td className="lo-col-month">
                          <span className="lo-month-name">{FULL_MONTH_NAMES[s.month - 1]}</span>
                          <span className="lo-month-year">{s.year}</span>
                        </td>
                        <td className="lo-col-lessons">{s.totalLessons || 0}</td>
                        <td className="lo-col-earnings">${(s.totalEarnings || 0).toFixed(2)}</td>
                        <td className="lo-col-status">
                          <span className={`lo-badge ${endCfg.className}`}>{endCfg.label}</span>
                        </td>
                        <td className="lo-col-payment">
                          <span className={`lo-badge ${payCfg.className}`}>{payCfg.label}</span>
                        </td>
                        <td className="lo-col-action">
                          <span className="lo-view-link">View →</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lo-mobile-only lo-card-list">
              {filteredSummaries.map((s) => {
                const endCfg = STATUS_CONFIG[s.endorsementStatus] || STATUS_CONFIG.pending;
                const payCfg = PAYMENT_CONFIG[s.paymentStatus] || PAYMENT_CONFIG.unpaid;
                return (
                  <div
                    key={`${s.year}-${s.month}`}
                    className="lo-mobile-card"
                    onClick={() => handleRowClick(s)}
                  >
                    {/* Left: month */}
                    <div className="lo-mc-month">
                      <span className="lo-mc-month-name">{MONTH_NAMES[s.month - 1]}</span>
                      <span className="lo-mc-month-year">{s.year}</span>
                    </div>
                    {/* Middle: earnings + lessons */}
                    <div className="lo-mc-mid">
                      <span className="lo-mc-earnings">${(s.totalEarnings || 0).toFixed(2)}</span>
                      <span className="lo-mc-lessons">{s.totalLessons || 0} lesson{s.totalLessons !== 1 ? 's' : ''}</span>
                    </div>
                    {/* Right: badges + arrow */}
                    <div className="lo-mc-right">
                      <div className="lo-mc-badges">
                        <span className={`lo-badge ${endCfg.className}`}>{endCfg.label}</span>
                        <span className={`lo-badge ${payCfg.className}`}>{payCfg.label}</span>
                      </div>
                      <span className="lo-view-link">→</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LessonOverview;
